import styles from './index.module.css'

import React from 'react'

import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import {
  addNativeTagToTracks,
  removeNativeTagFromTracks,
} from '@disco/state-management/actions/PlaylistTrackActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getNativeCategories } from '@disco/state-management/selectors/tags'

import type { NativeCategory, NativeTag, Track, TrackId } from 'types/track'
import type { PopoverItemType } from 'types/ui'

import { isAiTagPlaceholder } from 'components/common/add-track-tags-modal/ai-tag-placeholder'
import { Table } from 'components/ds'
import Icon from 'components/ui/icon'
import PopoverButton from 'components/ui/popover-button'
import Tag from 'components/ui/tag'

import { useDispatch, useSelector } from 'hooks/use-redux'

import CategoryTagPicker from './category-tag-picker'

type CountedTag = {
  tag: NativeTag
  count: number
}
const MENU_REMOVE: PopoverItemType = {
  icon: 'close',
  value: 'close',
  label: 'Remove from all',
}
const MENU_ADD: PopoverItemType = {
  icon: 'check',
  value: 'check',
  label: 'Add to all',
}
const MAX_DISPLAY_TAGS: number = 25
type Props = {
  tracks: Track[]
}

/**
 * Tags tab of the edit-metadata modal: a category-by-category table where
 * each row lists the tags already applied and a scoped "+ Add" popover for
 * picking more from that category. Supports both single-track editing and
 * bulk editing across multiple selected tracks (tags only some tracks
 * share get an Add to all / Remove from all menu instead of a plain chip).
 */
const NativeTagsTab = ({ tracks }: Props): React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const isNativeTagsReadonly: boolean | null | undefined = useSelector(
    state => state.user.business?.private_metadata.is_native_tags_readonly,
  )
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const nativeCategories: NativeCategory[] = useSelector(getNativeCategories)
  const trackIds: TrackId[] = tracks.map((track: Track) => track.id)
  const isBulk: boolean = tracks.length > 1
  const assignedTags: CountedTag[] = []
  tracks.forEach((track: Track) => {
    const tags = track.native_tags || []
    tags.forEach((tag: NativeTag) => {
      let assigned: CountedTag | null | undefined = assignedTags.find(
        (countedTag: CountedTag) => countedTag.tag.id === tag.id,
      )

      if (!assigned) {
        assigned = {
          tag,
          count: 0,
        }
        assignedTags.push(assigned)
      }

      assigned.count += 1
    })
  })

  const rows = nativeCategories.slice(0, MAX_DISPLAY_TAGS).map(cat => {
    const values: CountedTag[] = assignedTags.filter(
      ({ tag }: CountedTag) => tag.category_id === cat.id,
    )
    const chips = values.map((countedTag: CountedTag) => {
      const tag: NativeTag = countedTag.tag

      if (isBulk) {
        const handleMixedMenuClick = (item: PopoverItemType) => {
          switch (item.value) {
            case MENU_REMOVE.value:
              dispatch(removeNativeTagFromTracks(trackIds, tag))
              break

            case MENU_ADD.value:
              mixpanel.sendPureEvent('Add multiple native tags', {
                Tags: [tag.name],
              })
              dispatch(addNativeTagToTracks(trackIds, tag))
              break
          }
        }

        const mixed: boolean = countedTag.count < tracks.length
        const menuButton: React.ReactNode = mixed ? (
          <PopoverButton
            key="remove"
            iconNode={<Icon type="remove" width="12" height="12" />}
            items={[MENU_REMOVE, MENU_ADD]}
            onItemClick={handleMixedMenuClick}
          />
        ) : (
          <PopoverButton
            key="check"
            iconNode={<Icon type="check" width="12" height="12" />}
            items={[MENU_REMOVE]}
            onItemClick={handleMixedMenuClick}
          />
        )
        return (
          <Tag
            key={tag.id}
            tag={tag.name}
            tagColor={colorIndicatorsEnabled ? tag.color : undefined}
            ai={isAiTagPlaceholder(tag)}
            variant="surface"
            size="3"
            action={menuButton}
          />
        )
      }

      return (
        <Tag
          key={tag.id}
          tag={tag.name}
          tagColor={colorIndicatorsEnabled ? tag.color : undefined}
          ai={isAiTagPlaceholder(tag)}
          variant="surface"
          size="3"
          onRemoveClick={
            isNativeTagsReadonly
              ? undefined
              : () => {
                  dispatch(removeNativeTagFromTracks(trackIds, tag))
                }
          }
        />
      )
    })

    return (
      <Table.Row key={cat.id} className={styles.row}>
        <Table.RowHeaderCell className={styles.categoryCell}>
          <div className={styles.categoryHead}>
            <span>
              {cat.name}
              <span className={styles.categoryCount}>{values.length}</span>
            </span>
            {!isNativeTagsReadonly && (
              <CategoryTagPicker
                category={cat}
                trackIds={trackIds}
                assignedTagIds={values.map(({ tag }) => tag.id)}
                colorIndicatorsEnabled={colorIndicatorsEnabled}
                isBulk={isBulk}
              />
            )}
          </div>
        </Table.RowHeaderCell>
        <Table.Cell className={styles.tagsCell}>
          <div className={styles.chips}>{chips}</div>
        </Table.Cell>
      </Table.Row>
    )
  })

  return (
    <div className={styles.pane}>
      <Table.Root className={styles.table} variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell className={styles.categoryCell}>
              Category
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Tags</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{rows}</Table.Body>
      </Table.Root>
    </div>
  )
}

export default NativeTagsTab
